import React, { memo, useState } from "react";
import { CardText, Modal, ModalBody, ModalHeader, Spinner } from "reactstrap";
import { Check } from "react-feather";
import PropTypes from "prop-types";
import { genericPost, genericPut } from "services/services.general";
import CustomCheckbox from "components/customs/checkbox";
import styled from "styled-components";
import { AddButton } from "assets/js/Styles";
import FormGenerator from "components/customs/form-generator/input";

const BtnSection = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 3rem;
  padding-bottom: 4rem;
  gap: 16px;
`;

const ModalBodyContainer = styled.div`
  margin-bottom: 1rem;
`;

const StyledModalBody = styled(ModalBody)`
  margin-left: 1rem;
  margin-right: 1rem;
`;

const StyledCardText = styled(CardText)`
  margin-top: 1rem;
  text-align: center;
`;

const TableContainer = styled.div`
  margin-top: 1rem;
  height: 500px;
`;

const CheckboxGroup = styled.div`
  gap: 1rem;
`;

const StyledAddButton = styled(AddButton)`
  background: ${({ theme }) => theme.colors.buttonFormBlue};
  width: 100px;
`;

const StyledModal = styled(Modal)`
  &--container {
    padding: 0 !important;
    margin-bottom: 1rem !important;
  }

  .msg-body {
    margin-top: 1rem;

    .checkbox-group {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 1rem;
      margin-bottom: 1rem;
    }

    textarea {
      background: #f5f5f7;
    }
  }

  .btn-save {
    background: #00316d;
    box-shadow: 0px 2px 7px rgba(43, 53, 133, 0.27);
    border-radius: 11px;
    height: 45px;
    width: 120px;
  }
`;

const ConfirmModal = ({
  isOpen,
  event,
  handleClose,
  fetchComments,
  fetchDataTable,
  eventId,
  view,
}) => {
  const [reason, setReason] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [selectedAttendee, setSelectedAttendee] = useState("myself");

  const invitedText =
    selectedAttendee === "myself"
      ? "I will be attending the event"
      : "My representative/staff will be attending the event";

  const handleSubmit = async () => {
    const newStatus = "Confirmed";
    const data = event;
    data.eventParticipantStatus = newStatus;

    const trimmedReason = reason.trim();

    const finalComment = trimmedReason
      ? `${invitedText}. ${trimmedReason}`
      : invitedText;

    setIsLoading(true);

    // Wrap genericPost in a promise to wait for callback
    const postSuccess = await new Promise((resolve) => {
      genericPost({
        url: "/comment",
        formData: {
          text: finalComment,
          recipientId: null,
          newsId: null,
          eventId,
        },
        callback: ({ isSuccess }) => {
          if (isSuccess) {
            setReason("");
            if (view === "rep") fetchComments(eventId);
          }
          resolve(isSuccess);
        },
      });
    });

    if (postSuccess) {
      await new Promise((resolve) => {
        genericPut({
          url: `/eventsparticipant/${data.id}`,
          formData: {
            eventParticipantStatus: newStatus,
          },
          callback: ({ isSuccess }) => {
            if (isSuccess) {
              setReason("");
              fetchDataTable();
            }
            resolve();
          },
        });
      });

      handleClose();
    }

    setIsLoading(false);
  };

  return (
    <StyledModal
      isOpen={isOpen}
      toggle={handleClose}
      centered
    >
      <ModalHeader toggle={handleClose}>{"Confirm Event"}</ModalHeader>
      <ModalBody>
        <StyledModalBody>
          <ModalBodyContainer>
            <StyledCardText>
              {
                "Please signify if you are attending this event or if a representative or staff will attend on your behalf"
              }
            </StyledCardText>

            <TableContainer>
              <CheckboxGroup>
                <CustomCheckbox
                  label="Myself"
                  icon={<Check size={16} />}
                  checked={selectedAttendee === "myself"}
                  onChange={() => setSelectedAttendee("myself")}
                />

                <CustomCheckbox
                  label="Staff"
                  icon={<Check size={16} />}
                  checked={selectedAttendee === "staff"}
                  onChange={() => setSelectedAttendee("staff")}
                />
              </CheckboxGroup>
              <FormGenerator
                type="textarea"
                rows={5}
                value={reason}
                disabled={isLoading}
                onChange={(e) => {
                  const { value } = e.target;

                  setReason(value);
                }}
                placeholder="Add Comment"
              />
            </TableContainer>
          </ModalBodyContainer>
          <BtnSection>
            <StyledAddButton
              onClick={() => handleSubmit()}
              disabled={isLoading}
            >
              {isLoading ? (
                <Spinner
                  animation="border"
                  color="light"
                  size="sm"
                />
              ) : (
                "Submit"
              )}
            </StyledAddButton>
          </BtnSection>
        </StyledModalBody>
      </ModalBody>
    </StyledModal>
  );
};

ConfirmModal.prototype = {
  isOpen: PropTypes.bool,
  data: PropTypes.oneOfType([PropTypes.string]),
  handleClose: PropTypes.func,
  type: PropTypes.string,
  messageType: PropTypes.string,
};

export default memo(ConfirmModal);
